/**
 * The AI ask: a question, two sentences, and two buttons of one size (#399, CS-D1 to CS-D5, CO-D1).
 *
 * One screen in two forms, both drawn from `R2-2-web-ai`:
 *
 * - **`first_run`**: the step after first run's summary and before "Ready to go". The question alone.
 * - **`never_asked`**: the one ask of an account that finished first run before it was asked (CS-D5,
 *   `R2-existing-web`): "BEFORE YOU GO ON" and two short sentences above the question.
 *
 * Nothing else asks (CO-D1): an answer of any version is never asked again, so there is no "changed
 * text" form any more.
 *
 * Groundwork asks, not Yami Kuro: no mascot, no speech bubble (CS-D16). Nothing is preset, the two
 * buttons are the same size and shape (one filled, one outlined), and going on needs one of them
 * (CS-D3). **The second button is an answer**: it records a decline for the version shown, so nobody
 * leaves this screen as "never asked". There is no way back and none is needed, since an answer is
 * changed in Settings.
 *
 * The answer is saved before the screen gives way. A failed save keeps the screen, says so in the
 * app's own line for a write (`writeErrorLine`: the busy sentence when the server was busy) and
 * leaves both buttons for another try; nothing is recorded until the server has said so, and a second
 * tap while the first is in flight is not a second answer (`inFlight`). The buttons are `aria-disabled`
 * while one answer is being stored, never `disabled`: a disabled button drops the keyboard's focus to
 * the page, and the person who just heard "couldn't save that" would land nowhere.
 *
 * The strings are the deck's, none is written here; the full text behind the chip is
 * `ConsentFullText`. It records the deck's version (`CONSENT_TEXT_VERSION`), never the server's.
 *
 * The panel around it, the artboard's, is `AskStepFrame`'s (shared with the analytics step, #709).
 */

import { Info } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { useAnswerConsent, type ConsentAnswer } from '../../api/consents'
import { CONSENT_TEXT_VERSION, COPY, fill } from '../../copy/firstRun'
import { aiConsentText, aiFullText } from './consentCopy'
import type { ConsentAsk } from './consentRule'
import { ConsentFullText } from './ConsentFullText'
import { AnswerButton } from './AnswerButton'
import { AskStepFrame } from './AskStepFrame'
import { FOCUS_RING } from '../ui/focusRing'
import { WriteFailureNote } from './WriteFailureNote'

export type AiConsentVariant = 'first_run' | ConsentAsk

interface Props {
  variant: AiConsentVariant
  /** Called once the server has stored the answer. First run moves to "Ready to go"; the gate needs nothing. */
  onAnswered?: (answer: ConsentAnswer) => void
}

export function AiConsentAsk({ variant, onAnswered }: Props) {
  const version = CONSENT_TEXT_VERSION.aiProcessing
  const text = aiConsentText(version)
  const answerConsent = useAnswerConsent()
  const [textOpen, setTextOpen] = useState(false)
  const [pressed, setPressed] = useState<ConsentAnswer | null>(null)
  // True from the first tap until the server has answered, before React has re-rendered the buttons as
  // busy: two taps in one tick would otherwise both reach `mutate`. Same reason as FirstRun's
  // `finishingRef`.
  const inFlight = useRef(false)
  const headingRef = useRef<HTMLHeadingElement>(null)

  // The question takes focus when the screen arrives, so a keyboard or screen-reader user who has just
  // pressed "Looks good" (which leaves the page) or opened the app lands on it, not on the page behind.
  // Neither answer is focused: nothing is preset (CS-D3).
  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  async function answer(choice: ConsentAnswer) {
    if (inFlight.current) return
    inFlight.current = true
    setPressed(choice)
    try {
      await answerConsent.mutateAsync({ kind: 'ai_processing', answer: choice, version })
    } catch {
      // The failure is on `answerConsent.error`, drawn below; the buttons come back, and focus never left.
      inFlight.current = false
      setPressed(null)
      return
    }
    // Stored. The caller moves on, or the gate gives way when `me` changes; the screen stays as it is
    // meanwhile, so nothing flashes between the answer and what follows.
    onAnswered?.(choice)
  }

  const existing = variant !== 'first_run'
  const busy = pressed !== null

  return (
    <AskStepFrame
      aside={
        <ConsentFullText
          text={aiFullText(version)}
          open={textOpen}
          onClose={() => {
            setTextOpen(false)
          }}
        />
      }
    >
      {existing && (
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-(--text-cream-soft)">
          {COPY.consentBeforeYouGoOn}
        </p>
      )}
      {existing && (
        <p className="mt-4 max-w-[46ch] text-pretty text-[15px] leading-[1.55] text-(--text-cream)">
          {COPY.consentExistingLead}
        </p>
      )}

      <h1
        ref={headingRef}
        tabIndex={-1}
        className={`${existing ? 'mt-4' : 'mt-2'} max-w-[16ch] text-balance outline-none text-[30px] font-light leading-[1.1] tracking-[-0.02em] text-(--text-cream) sm:text-[34px]`}
        style={{ fontFamily: 'var(--font-display)' }}
      >
        {COPY.consentAiTitle}
      </h1>

      <p className="mt-5 max-w-[46ch] text-pretty text-base leading-[1.55] text-(--text-cream)">
        {text.body}
      </p>

      <button
        type="button"
        onClick={() => setTextOpen(true)}
        aria-haspopup="dialog"
        className={`mt-4 inline-flex min-h-11 items-center gap-2 self-start rounded-full border border-(--text-cream-border) bg-(--text-cream-hairline) px-4 text-sm font-medium text-(--text-cream) transition-[border-color,transform] duration-150 hover:border-(--text-cream-dim) active:scale-[0.98] ${FOCUS_RING}`}
      >
        <Info size={14} strokeWidth={1.5} aria-hidden className="text-(--text-cream-soft)" />
        {COPY.consentAiDetail}
      </button>

      {/* Content grows upward and the buttons hold the floor on a phone, as on every first-run screen. */}
      <div className="min-h-8 flex-1 md:min-h-10 md:flex-none" />

      {answerConsent.isError && <WriteFailureNote error={answerConsent.error} className="mb-3" />}

      <div className="flex flex-col gap-3 sm:flex-row">
        <AnswerButton
          filled
          label={COPY.consentAgree}
          saving={pressed === 'grant'}
          busy={busy}
          onClick={() => void answer('grant')}
        />
        <AnswerButton
          label={COPY.consentAiDecline}
          saving={pressed === 'decline'}
          busy={busy}
          onClick={() => void answer('decline')}
        />
      </div>
      {/* The floor's own padding sits under the LAST line, so the caption clears the home indicator. */}
      <p className="mt-3.5 pb-[max(24px,env(safe-area-inset-bottom))] text-xs text-(--text-cream-soft) md:pb-0">
        {fill(COPY.consentChangeable, { n: version })}
      </p>
    </AskStepFrame>
  )
}
